import type { PricingData } from '../types'
import { useEffect, useRef, useState } from 'react'
import { formatPrice } from '../utils/helpers'

const SCROLL_TOP_RELOAD_KEY = 'unico-scroll-top-on-reload'

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 3.2 3.5 10v10.3c0 .4.3.7.7.7h5.1c.4 0 .7-.3.7-.7v-5.3h4v5.3c0 .4.3.7.7.7h5.1c.4 0 .7-.3.7-.7V10L12 3.2Z"
      fill="currentColor"
    />
  </svg>
)

interface FloatingPriceProps {
  pricing: PricingData
  onShowSummary: () => void
  isConfigurationValid?: boolean
  canShowPrice?: boolean
  hasModel?: boolean
  priceMode: 'netto' | 'vat8' | 'vat23'
  setPriceMode: (mode: 'netto' | 'vat8' | 'vat23') => void
}

const FloatingPrice = ({ pricing, onShowSummary, isConfigurationValid = true, canShowPrice = false, hasModel = false, priceMode, setPriceMode }: FloatingPriceProps) => {
  // Show floating price if we have a model, regardless of validation
  if (!hasModel) return null

  const [menuOpen, setMenuOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const [isHomeConfirmOpen, setIsHomeConfirmOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (window.sessionStorage.getItem(SCROLL_TOP_RELOAD_KEY)) {
      window.sessionStorage.removeItem(SCROLL_TOP_RELOAD_KEY)
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!(e.target instanceof Node)) return
      if (!menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const handleSummaryClick = () => {
    if (isConfigurationValid) {
      onShowSummary()
    } else {
      // This will be handled by the parent component
      onShowSummary()
    }
  }

  const handleHomeClick = () => {
    setIsHomeConfirmOpen(true)
  }

  const handleConfirmHomeClick = () => {
    setIsHomeConfirmOpen(false)
    window.sessionStorage.setItem(SCROLL_TOP_RELOAD_KEY, '1')
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    window.location.reload()
  }

  const handleCloseHomeConfirm = () => {
    setIsHomeConfirmOpen(false)
  }

  const vatMultiplier = priceMode === 'vat8' ? 1.08 : priceMode === 'vat23' ? 1.23 : 1
  const rawPrice = canShowPrice ? pricing.totalPrice : 0
  const displayPrice = Math.round(rawPrice * vatMultiplier)

  const label = priceMode === 'netto' 
    ? 'CENA NETTO:' 
    : priceMode === 'vat8' 
      ? 'CENA BRUTTO (8% VAT):' 
      : 'CENA BRUTTO (23% VAT):'

  return (
    <>
      <div className={`floating-price ${isHidden ? 'hidden' : ''}`} id="floating-price">
        {isHidden && (
          <div className="floating-price-collapsed-controls">
            <button
              type="button"
              className="floating-price-icon-button floating-price-home floating-price-home-collapsed"
              aria-label="Odśwież stronę i wróć na górę"
              onClick={handleHomeClick}
              title="Odśwież stronę i wróć na górę"
            >
              <HomeIcon />
            </button>
            <div className="floating-price-tab" onClick={() => setIsHidden(false)}>
              cena
            </div>
          </div>
        )}
        <div className="floating-price-content" ref={menuRef}>
          <button
            type="button"
            className="floating-price-icon-button floating-price-home"
            aria-label="Odśwież stronę i wróć na górę"
            onClick={handleHomeClick}
            title="Odśwież stronę i wróć na górę"
          >
            <span className="floating-price-home-text">
              <span>Powrót do początku</span>
              <span>konfiguracji</span>
            </span>
            <HomeIcon />
          </button>
          <div className="floating-price-actions">
            <div className="floating-price-left-actions">
              <button 
                type="button" 
                className="floating-price-icon-button floating-price-hide" 
                aria-label="Ukryj cenę"
                onClick={() => setIsHidden(true)}
                title="Ukryj cenę"
              >
                −
              </button>
            </div>
            <button 
              type="button" 
              className="floating-price-icon-button floating-price-gear" 
              aria-label="Opcje ceny"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
              title="Opcje ceny"
            >
              VAT 8/23
            </button>
            {menuOpen && (
              <div className="floating-price-menu" role="menu">
                <button 
                  type="button" 
                  className={`menu-item ${priceMode === 'netto' ? 'active' : ''}`} 
                  onClick={() => { setPriceMode('netto'); setMenuOpen(false) }}
                  role="menuitem"
                >
                  {priceMode === 'netto' ? '✓ ' : ''}NETTO
                </button>
                <button 
                  type="button" 
                  className={`menu-item ${priceMode === 'vat8' ? 'active' : ''}`} 
                  onClick={() => { setPriceMode('vat8'); setMenuOpen(false) }}
                  role="menuitem"
                >
                  {priceMode === 'vat8' ? '✓ ' : ''}8% VAT
                </button>
                <button 
                  type="button" 
                  className={`menu-item ${priceMode === 'vat23' ? 'active' : ''}`} 
                  onClick={() => { setPriceMode('vat23'); setMenuOpen(false) }}
                  role="menuitem"
                >
                  {priceMode === 'vat23' ? '✓ ' : ''}23% VAT
                </button>
              </div>
            )}
          </div>
          <div className="floating-price-label">{label}</div>
          <div className="floating-price-value" id="floating-total-price">
            {displayPrice > 0 ? formatPrice(displayPrice) + ' zł' : 'Wybierz wariant'}
          </div>
          <div className="floating-price-note">
            {displayPrice > 0 ? '+ transport i montaż' : 'aby zobaczyć cenę'}
          </div>
          <button 
            className={`floating-price-btn ${!isConfigurationValid ? 'disabled' : ''}`} 
            onClick={handleSummaryClick}
            disabled={!isConfigurationValid}
          >
            PODSUMOWANIE
          </button>
        </div>
      </div>

      {isHomeConfirmOpen && (
        <div className="modal-overlay" onClick={handleCloseHomeConfirm} style={{ zIndex: 10001 }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-confirm-title"
            style={{ maxWidth: '520px', borderRadius: 0 }}
          >
            <div className="modal-header" style={{ paddingBottom: '12px' }}>
              <h2 id="home-confirm-title" style={{ width: '100%', textAlign: 'center' }}>Potwierdź działanie</h2>
              <button className="modal-close" onClick={handleCloseHomeConfirm} aria-label="Zamknij">
                ×
              </button>
            </div>
            <div className="modal-body" style={{ paddingTop: 0, textAlign: 'center' }}>
              <p style={{ marginTop: 0, lineHeight: 1.5 }}>
                Strona zostanie odświeżona, widok wróci na samą górę do wyboru kategorii, a obecne ustawienia konfiguracji zostaną utracone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <button type="button" className="btn-secondary btn-no-radius" onClick={handleCloseHomeConfirm}>
                  Anuluj
                </button>
                <button type="button" className="btn-primary btn-no-radius" onClick={handleConfirmHomeClick}>
                  Tak, odśwież
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FloatingPrice
